import type { Road, World } from '../../shared/types.js';
import { ROAD_SPECS } from '../../shared/osm/classify.js';

/**
 * Which streets meet at each junction, and which way they leave.
 *
 * The generator already emits a disc at every OSM node shared by two or more drivable ways,
 * but a disc is a position and a radius and nothing else: it does not say how many streets
 * meet there or where they go. Crossings and traffic lights both need exactly that, and both
 * need the same answer, so it is worked out once here and handed to both.
 *
 * The join is on coordinates rather than on node ids, which sounds fragile and is not.
 * `Junction.x` is `round3` of a road vertex that was itself already `round3`, and rounding
 * to the millimetre is idempotent — so a junction's centre is *bit-identical* to the road
 * vertex it came from, and to the same vertex on every other way through that node, because
 * they all project from one pair of coordinates. The alternative was carrying node ids into
 * the payload for the sake of two pieces of street furniture.
 */

export interface JunctionArm {
  /** Unit vector along the street, pointing away from the junction centre. */
  dx: number;
  dz: number;
  /** Carriageway width of the street this arm belongs to. */
  width: number;
  roadId: number;
  /**
   * The road itself — a way clipped by the region boundary arrives as several roads sharing
   * one id, so the id alone does not say which one — the vertex of it the junction is at,
   * and which way along it the arm leaves.
   */
  road: Road;
  at: number;
  step: 1 | -1;
  /** Part of a roundabout's ring rather than a street leading onto it. */
  roundabout: boolean;
}

export interface JunctionNode {
  x: number;
  z: number;
  /** Disc radius — half the width of the widest street meeting here. */
  r: number;
  /** The height the tarmac is drawn at, before terrain. */
  y: number;
  /** The widest street meeting here. */
  maxWidth: number;
  arms: JunctionArm[];
}

/**
 * Two arms closer together than this are the same street counted twice.
 *
 * A street that OSM splits into two ways at the junction contributes one arm from each way,
 * and they point in opposite directions, which is correct. What is not correct is the case
 * where a way merely passes through a node another way also touches: both then offer an arm
 * down the same piece of tarmac, and without this the junction gets two crossings painted on
 * top of each other and two traffic lights in the same hole in the pavement. cos 15°.
 */
const SAME_ARM_DOT = 0.966;

export function junctionArms(world: World): JunctionNode[] {
  const byKey = new Map<string, JunctionNode>();
  for (const j of world.junctions) {
    byKey.set(`${j.x}|${j.z}`, { x: j.x, z: j.z, r: j.r, y: j.y, maxWidth: 0, arms: [] });
  }
  if (byKey.size === 0) return [];

  for (const road of world.roads) {
    if (!ROAD_SPECS[road.cls].drivable) continue;
    const n = road.pts.length / 2;
    for (let i = 0; i < n; i++) {
      const node = byKey.get(`${road.pts[i * 2]}|${road.pts[i * 2 + 1]}`);
      if (!node) continue;
      if (road.width > node.maxWidth) node.maxWidth = road.width;

      // A junction in the middle of a way sends the street off in both directions; one at
      // the end of a way sends it off in one.
      for (const k of [i - 1, i + 1]) {
        if (k < 0 || k >= n) continue;
        const dx = road.pts[k * 2] - road.pts[i * 2];
        const dz = road.pts[k * 2 + 1] - road.pts[i * 2 + 1];
        const len = Math.hypot(dx, dz);
        if (len < 1e-3) continue;
        const ux = dx / len;
        const uz = dz / len;
        if (node.arms.some((a) => a.dx * ux + a.dz * uz > SAME_ARM_DOT)) continue;
        node.arms.push({
          dx: ux,
          dz: uz,
          width: road.width,
          roadId: road.id,
          road,
          at: i,
          step: k > i ? 1 : -1,
          roundabout: road.roundabout === true,
        });
      }
    }
  }

  return [...byKey.values()];
}
